import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  Plus,
  Search,
  ChevronRight,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select, Textarea } from '../components/ui/Input.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

export type QuestionnaireType = 'poll' | 'quiz' | 'survey';
export type QuestionnaireStatus = 'draft' | 'published' | 'closed';

export interface Questionnaire {
  id: string;
  type: QuestionnaireType;
  status: QuestionnaireStatus;
  title: string;
  slug: string;
  description: string | null;
  startAt: string | null;
  endAt: string | null;
  responseCount: number;
  createdAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TYPE_TABS = [
  { key: 'all', label: 'All' },
  { key: 'poll', label: 'Polls' },
  { key: 'quiz', label: 'Quizzes' },
  { key: 'survey', label: 'Surveys' },
] as const;

const STATUS_OPTIONS = [
  { key: 'all', label: 'All Statuses' },
  { key: 'draft', label: 'Draft' },
  { key: 'published', label: 'Published' },
  { key: 'closed', label: 'Closed' },
] as const;

function typeVariant(type: QuestionnaireType) {
  if (type === 'poll') return 'info';
  if (type === 'quiz') return 'purple';
  return 'warning'; // survey → amber
}

function typeLabel(type: QuestionnaireType) {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function statusVariant(status: QuestionnaireStatus) {
  if (status === 'published') return 'success';
  if (status === 'closed') return 'default';
  return 'warning'; // draft
}

function fmt(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// ─── Create Modal ─────────────────────────────────────────────────────────────

function CreateQuestionnaireModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const toast = useToast();
  const [type, setType] = useState<QuestionnaireType>('poll');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  function handleTitleChange(value: string) {
    setTitle(value);
    if (!slugTouched) {
      setSlug(slugify(value));
    }
  }

  function handleSlugChange(value: string) {
    setSlugTouched(true);
    setSlug(slugify(value));
  }

  const createMutation = useMutation({
    mutationFn: () =>
      api
        .post<Questionnaire>('/api/v1/questionnaires', {
          type,
          title: title.trim(),
          slug: slug.trim() || slugify(title.trim()),
          description: description.trim() || undefined,
        })
        .then((r) => r.data),
    onSuccess: (data) => {
      toast.success('Questionnaire created');
      onCreated(data.id);
    },
    onError: (err) => toast.error(extractError(err, 'Failed to create questionnaire')),
  });

  return (
    <Modal open onClose={onClose} title="New Questionnaire" size="md">
      <div className="space-y-4">
        <Select
          label="Type"
          value={type}
          onChange={(e) => setType(e.target.value as QuestionnaireType)}
        >
          <option value="poll">Poll</option>
          <option value="quiz">Quiz</option>
          <option value="survey">Survey</option>
        </Select>

        <Input
          label="Title"
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="e.g. Customer Satisfaction Survey"
        />

        <Input
          label="Slug"
          value={slug}
          onChange={(e) => handleSlugChange(e.target.value)}
          placeholder="auto-generated from title"
          hint="URL-safe identifier"
        />

        <Textarea
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Brief description of this questionnaire"
        />

        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={createMutation.isPending}
            disabled={!title.trim()}
            onClick={() => createMutation.mutate()}
          >
            Create
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function QuestionnairesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [typeTab, setTypeTab] = useState<'all' | QuestionnaireType>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | QuestionnaireStatus>('all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['questionnaires', typeTab, statusFilter, search],
    queryFn: () =>
      api
        .get<Questionnaire[]>('/api/v1/questionnaires', {
          params: {
            type: typeTab !== 'all' ? typeTab : undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: search.trim() || undefined,
          },
        })
        .then((r) => r.data),
    staleTime: 30_000,
  });

  const questionnaires = data ?? [];

  function handleCreated(id: string) {
    setShowCreate(false);
    queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
    navigate(`/questionnaires/${id}`);
  }

  return (
    <div>
      <PageHeader
        title="Questionnaires"
        description="Create and manage polls, quizzes, and surveys."
        action={
          <Button onClick={() => setShowCreate(true)} size="sm">
            <Plus size={15} />
            New Questionnaire
          </Button>
        }
      />

      {/* ── Filters ── */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        {/* Type tabs */}
        <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
          {TYPE_TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTypeTab(key as typeof typeTab)}
              className={
                typeTab === key
                  ? 'px-3 py-1 rounded-md text-sm font-medium bg-white text-zinc-900 shadow-sm'
                  : 'px-3 py-1 rounded-md text-sm font-medium text-zinc-500 hover:text-zinc-700'
              }
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:ml-auto">
          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className="h-8 px-2.5 text-xs border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          >
            {STATUS_OPTIONS.map(({ key, label }) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>

          {/* Search */}
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title…"
              className="h-8 pl-8 pr-3 text-xs border border-zinc-200 rounded-lg bg-white text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent w-48"
            />
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100 bg-zinc-50">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                Title
              </th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                Type
              </th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                Status
              </th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                Responses
              </th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                Date range
              </th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {isLoading && (
              <tr>
                <td colSpan={6} className="p-0">
                  <SkeletonRows count={5} cols={6} />
                </td>
              </tr>
            )}

            {isError && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-red-500">
                  Failed to load questionnaires.
                </td>
              </tr>
            )}

            {!isLoading && !isError && questionnaires.length === 0 && (
              <tr>
                <td colSpan={6}>
                  <EmptyState
                    icon={<ClipboardList size={28} />}
                    title="No questionnaires yet"
                    description="Create your first poll, quiz, or survey to engage your audience."
                    action={{ label: 'New Questionnaire', onClick: () => setShowCreate(true) }}
                  />
                </td>
              </tr>
            )}

            {questionnaires.map((q) => (
              <tr
                key={q.id}
                onClick={() => navigate(`/questionnaires/${q.id}`)}
                className="hover:bg-zinc-50 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3">
                  <p className="font-medium text-zinc-900 truncate max-w-xs">{q.title}</p>
                  <p className="text-xs text-zinc-400 mt-0.5 font-mono">{q.slug}</p>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={typeVariant(q.type)}>{typeLabel(q.type)}</Badge>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={statusVariant(q.status)} dot>
                    {q.status}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-zinc-600 tabular-nums">
                  {q.responseCount.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-zinc-500 text-xs whitespace-nowrap">
                  {q.startAt || q.endAt
                    ? `${fmt(q.startAt)} – ${fmt(q.endAt)}`
                    : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  <ChevronRight size={15} className="text-zinc-300 ml-auto" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateQuestionnaireModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
